import {
  PATIENT_QUICK_NOTE_SYNC_STALE_PENDING_SECONDS,
  type PatientQuickNoteHealthieSyncResponse,
} from "../../../shared/api-types";

export type QuickNoteSyncTone = "neutral" | "success" | "danger";

export interface QuickNoteSyncLine {
  tone: QuickNoteSyncTone;
  headline: string;
  detail: string;
  /** Sending is in progress: show a spinner and poll. */
  sending: boolean;
  /** The RD can send the current note again. */
  retryLabel: string | null;
}

/**
 * Plain words for the current Patient Quick Note's copy in Healthie's quick
 * notes (NO-750), mirroring the SOAP writeback delivery line. `null` means the
 * current version was never sent (the RD saved before Healthie sync was on).
 * Nothing here carries Healthie text or a Patient identifier.
 */
export function quickNoteSyncLine(
  state: PatientQuickNoteHealthieSyncResponse | null,
  nowMs: number = Date.now()
): QuickNoteSyncLine {
  if (state === null) {
    return {
      tone: "neutral",
      headline: "Not in Healthie yet.",
      detail: "Send this note to the patient's Healthie quick notes.",
      sending: false,
      retryLabel: "Send to Healthie",
    };
  }

  if (state.status === "pending" && stalePending(state, nowMs))
    return STALE_PENDING_LINE;

  switch (state.status) {
    case "pending":
    case "running":
      return {
        tone: "neutral",
        headline: "Sending to Healthie…",
        detail: "This can take a moment if Healthie is slow.",
        sending: true,
        retryLabel: null,
      };
    case "succeeded":
    case "superseded":
      return {
        tone: "success",
        headline: "Saved to Healthie.",
        detail: "It's in the patient's Healthie quick notes.",
        sending: false,
        retryLabel: null,
      };
    case "conflict":
      return conflictLine(state.code);
    case "failed":
      return failedLine(state.code);
  }
}

/**
 * A send that never reached the worker (its enqueue failed) stays pending.
 * Past the server's retry threshold the RD gets Retry instead of a spinner
 * that would never finish.
 */
const STALE_PENDING_LINE: QuickNoteSyncLine = {
  tone: "danger",
  headline: "Healthie hasn't picked this up yet.",
  detail: "Your note is saved here. Try sending it again.",
  sending: false,
  retryLabel: "Retry",
};

/**
 * How long until a fresh pending send turns stale, so the line can re-render
 * at that moment: a stranded row polls back identical data, which alone never
 * re-renders. Null when there is nothing to wait for.
 */
export function msUntilStalePending(
  state: PatientQuickNoteHealthieSyncResponse | null,
  nowMs: number = Date.now()
): number | null {
  if (state?.status !== "pending") return null;

  const updatedMs = Date.parse(state.updatedAt);
  if (!Number.isFinite(updatedMs)) return null;

  const windowMs = PATIENT_QUICK_NOTE_SYNC_STALE_PENDING_SECONDS * 1000;
  const remaining = updatedMs + windowMs - nowMs;

  // A client clock far behind the server's could ask for a longer wait than
  // setTimeout can hold, which browsers run immediately. Cap at that limit,
  // so the one timer still fires at the right moment for any smaller skew.
  return remaining >= 0 ? Math.min(remaining + 1, MAX_TIMER_DELAY_MS) : null;
}

/** The longest delay setTimeout honors (2^31 - 1 ms, about 24.8 days). */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

function stalePending(
  state: PatientQuickNoteHealthieSyncResponse,
  nowMs: number
): boolean {
  const updatedMs = Date.parse(state.updatedAt);

  return (
    Number.isFinite(updatedMs) &&
    nowMs - updatedMs > PATIENT_QUICK_NOTE_SYNC_STALE_PENDING_SECONDS * 1000
  );
}

function conflictLine(
  code: PatientQuickNoteHealthieSyncResponse["code"]
): QuickNoteSyncLine {
  if (code === "managed_block_marker_in_note") {
    return {
      tone: "danger",
      headline: "Not sent to Healthie.",
      detail: "This note contains an AlwaysAI marker line. Remove it, then retry.",
      sending: false,
      retryLabel: "Retry",
    };
  }

  return {
    tone: "danger",
    headline: "Not sent to Healthie.",
    detail:
      "The patient's Healthie quick notes have a damaged AlwaysAI section, so nothing was changed. Fix it in Healthie, then retry.",
    sending: false,
    retryLabel: "Retry",
  };
}

function failedLine(
  code: PatientQuickNoteHealthieSyncResponse["code"]
): QuickNoteSyncLine {
  if (code === "healthie_patient_unresolved" || code === "healthie_patient_ambiguous") {
    return {
      tone: "danger",
      headline: "Not sent to Healthie.",
      detail:
        "This patient isn't linked to one Healthie client. Ask the team to check the link.",
      sending: false,
      retryLabel: "Retry",
    };
  }

  return {
    tone: "danger",
    headline: "Couldn't update Healthie.",
    detail: "Your note is saved here. Try sending it again.",
    sending: false,
    retryLabel: "Retry",
  };
}
